/**
 * Features API Client
 *
 * API client for feature access control endpoints.
 */

import { api } from './api';

/**
 * Feature access result from the API
 */
export interface FeatureAccessResult {
  featureId: string;
  hasAccess: boolean;
  accessReason: string;
  expiresAt?: string;
  upgradeRequired?: string;
  value?: any;            // Resolved tier-specific value for non-boolean capabilities
  parentLocked?: boolean; // True if denied because parent capability is locked
}

/**
 * User feature access response
 */
export interface UserFeatureAccess {
  userId: string;
  tier: string;
  features: Record<string, FeatureAccessResult>;
  isLaunchPromo: boolean;
  fetchedAt: string;
}

/**
 * Feature configuration (public view)
 */
export interface PublicFeature {
  featureId: string;
  name: string;
  description: string;
  category: string;
  tierAccess: Record<string, boolean>;
}

/**
 * Full feature configuration (admin view)
 */
export interface FeatureConfig {
  featureId: string;
  name: string;
  description: string;
  category: string;
  type?: 'boolean' | 'number' | 'list' | 'object' | 'variant';
  parentId?: string;
  defaultEnabled: boolean;
  globalEnabled: boolean;
  tierAccess: Record<string, boolean>;
  tierValues?: Record<string, any>;
  automaticRules: AutomaticRule[];
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Automatic rule configuration
 */
export interface AutomaticRule {
  ruleId: string;
  type: string;
  priority: number;
  enabled: boolean;
  config: Record<string, any>;
}

/**
 * User feature override
 */
export interface UserFeatureOverride {
  userId: string;
  featureId: string;
  enabled: boolean;
  reason: string;
  grantedBy: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Feature access summary
 */
export interface FeatureAccessSummary {
  totalFeatures: number;
  enabledFeatures: number;
  disabledFeatures: number;
  byCategory: Record<string, number>;
}

/**
 * Tier comparison data
 */
export interface TierComparison {
  tier: string;
  level: number;
  featureCount: number;
}

/**
 * Features API for authenticated users
 */
export const featuresAPI = {
  /**
   * Get all accessible features for the current user
   */
  getAccessibleFeatures: async (): Promise<UserFeatureAccess> => {
    const response = await api.get('/features/access');
    return response.data;
  },

  /**
   * Check if the current user has access to a specific feature
   */
  checkFeatureAccess: async (featureId: string): Promise<FeatureAccessResult> => {
    const response = await api.post(`/features/${featureId}/check`);
    return response.data;
  },

  /**
   * Get list of all features (public metadata)
   */
  listFeatures: async (): Promise<{ features: PublicFeature[] }> => {
    const response = await api.get('/features/list');
    return response.data;
  },

  /**
   * Get tier comparison data (for pricing page)
   */
  getTierComparison: async (): Promise<{
    byCategory: Record<string, PublicFeature[]>;
    comparison: TierComparison[];
    tiers: string[];
  }> => {
    const response = await api.get('/features/tier-comparison');
    return response.data;
  },
};

/**
 * Admin Features API
 */
export const adminFeaturesAPI = {
  /**
   * List all features with full configuration
   */
  listAllFeatures: async (): Promise<{
    features: FeatureConfig[];
    summary: FeatureAccessSummary;
  }> => {
    const response = await api.get('/admin/features');
    return response.data;
  },

  /**
   * Get a specific feature configuration
   */
  getFeature: async (featureId: string): Promise<{ feature: FeatureConfig }> => {
    const response = await api.get(`/admin/features/${featureId}`);
    return response.data;
  },

  /**
   * Create a new feature
   */
  createFeature: async (feature: {
    featureId: string;
    name: string;
    description: string;
    category: string;
    defaultEnabled?: boolean;
    tierAccess?: Record<string, boolean>;
    automaticRules?: AutomaticRule[];
  }): Promise<{ feature: FeatureConfig }> => {
    const response = await api.post('/admin/features', feature);
    return response.data;
  },

  /**
   * Update a feature configuration
   */
  updateFeature: async (
    featureId: string,
    updates: {
      name?: string;
      description?: string;
      category?: string;
      defaultEnabled?: boolean;
      globalEnabled?: boolean;
      tierAccess?: Record<string, boolean>;
      automaticRules?: AutomaticRule[];
    }
  ): Promise<{ feature: FeatureConfig }> => {
    const response = await api.put(`/admin/features/${featureId}`, updates);
    return response.data;
  },

  /**
   * Delete a feature
   */
  deleteFeature: async (featureId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/features/${featureId}`);
    return response.data;
  },

  /**
   * Toggle global feature flag
   */
  toggleGlobalFeature: async (
    featureId: string,
    enabled: boolean
  ): Promise<{ feature: FeatureConfig }> => {
    const response = await api.post(`/admin/features/${featureId}/global`, { enabled });
    return response.data;
  },

  /**
   * Update tier access for a feature
   */
  updateTierAccess: async (
    featureId: string,
    tier: string,
    enabled: boolean
  ): Promise<{ feature: FeatureConfig }> => {
    const response = await api.post(`/admin/features/${featureId}/tier`, { tier, enabled });
    return response.data;
  },

  /**
   * Get user's feature overrides
   */
  getUserFeatures: async (
    userId: string
  ): Promise<{
    userId: string;
    email?: string;
    tier: string;
    registrationDate: string;
    subscriptionStartDate?: string;
    overrides: UserFeatureOverride[];
    featureAccess: Array<{
      featureId: string;
      name: string;
      category: string;
      hasAccess: boolean;
      accessReason: string;
      hasOverride: boolean;
      override: UserFeatureOverride | null;
    }>;
  }> => {
    const response = await api.get(`/admin/feature-overrides/user/${userId}`);
    return response.data;
  },

  /**
   * Grant a feature override to a user
   */
  grantOverride: async (
    userId: string,
    featureId: string,
    data: {
      enabled: boolean;
      reason: string;
      expiresAt?: string;
    }
  ): Promise<{ override: UserFeatureOverride }> => {
    const response = await api.post(`/admin/feature-overrides/user/${userId}/feature/${featureId}`, data);
    return response.data;
  },

  /**
   * Revoke a feature override from a user
   */
  revokeOverride: async (
    userId: string,
    featureId: string
  ): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/feature-overrides/user/${userId}/feature/${featureId}`);
    return response.data;
  },

  /**
   * Get feature audit logs
   */
  getAuditLogs: async (
    featureId?: string,
    limit?: number
  ): Promise<{
    auditLogs: Array<{
      timestamp: string;
      action: string;
      featureId: string;
      targetUserId?: string;
      targetTier?: string;
      performedBy: string;
      reason: string;
      previousState?: any;
      newState?: any;
    }>;
  }> => {
    const params: Record<string, any> = {};
    if (featureId) params.featureId = featureId;
    if (limit) params.limit = limit;
    const response = await api.get('/admin/features/audit', { params });
    return response.data;
  },

  /**
   * Seed initial feature configurations
   */
  seedFeatures: async (): Promise<{ message: string; count: number }> => {
    const response = await api.post('/admin/features/seed');
    return response.data;
  },

  /**
   * Get launch promo status
   */
  getLaunchPromo: async (): Promise<{ enabled: boolean; updatedAt?: string; updatedBy?: string }> => {
    const response = await api.get('/admin/launch-promo');
    return response.data;
  },

  /**
   * Toggle launch promo
   */
  setLaunchPromo: async (enabled: boolean): Promise<{ enabled: boolean; updatedAt: string; updatedBy: string }> => {
    const response = await api.post('/admin/launch-promo', { enabled });
    return response.data;
  },
};
